import type { Study, API } from '@pocketprep/types'
import { examMetadataModule } from '@/store/examMetadata/module'
import { runCloudFunction, Parse } from '@/store/parseUtils'
import { callApi, mapObjectIdField } from '@/store/apiUtils'
import * as Sentry from '@sentry/vue'
import { userModule } from '@/store/user/module'
import { questionModule } from '@/store/question/module'
import { fetchLoadable, resetLoadable } from '@/store/utils'
import { qotdModule } from '@/store/qotd/module'
import type { ClientQuestion, TQuizDraft } from '@/store/qotd/state'
import { quizModule } from '@/store/quiz/module'
import { analyticsModule } from '@/store/analytics/module'
import { localISODateString } from '@/utils'

/**
 * Reshape a server-nest QotD question into a `ClientQuestion` for the
 * `QuestionJSON | ClientQuestion` state union the rest of the client reads:
 * re-add `objectId` and re-expand the resolved `subject`.
 * @todo temporary until consumers read `API.Question` directly (WEB-2275)
 */
const mapNestQotDQuestion = (
    question: API.Question,
    subject: API.Subject
): ClientQuestion => ({
    ...mapObjectIdField(question),
    subject: mapObjectIdField(subject),
})

const fetchQotDQuestion = async ({ examGuid, examMetadataId, version }: {
    examGuid: string
    examMetadataId: string
    version: string
}) => {
    // Get today's QotD
    const today = new Date()
    const daysSinceEpoch = Math.floor((today.getTime() / 60000 - today.getTimezoneOffset()) / 60 / 24)

    // old parse cloud function fallback for when nest is not available, or if the question is missing from nest
    const fetchParseQotDQuestion = async () => (await runCloudFunction<Study.Cloud.fetchQotDV5>('fetchQotD-v5', {
        daysSinceEpoch,
        examGuid,
        examMetadataId,
        version,
        includeMatrixQuestions: true,
        includeMPMCQuestions: true,
        includeBuildListQuestions: true,
    }))[0]

    const nestApiDomain = import.meta.env.VUE_APP_NEST_API_URL_DOMAIN
    if (!nestApiDomain) {
        return fetchParseQotDQuestion()
    }

    // Once we kill the Parse server this will be the only code path
    const params: API.FetchQotdParams = { daysSinceEpoch }
    const response: API.FetchQotdResponse = await callApi({
        method: 'GET',
        url: `/exams/${examMetadataId}/qotd`,
        params,
    })
    const question = response.questions[0]
    if (!question) {
        return
    }

    // Nest returns subjects alongside the question; if the match is missing, report it and
    // fall back to the Parse loader rather than shipping a question with no subject.
    const subject = response.subjects.find(s => s.id === question.subjectId)
    if (!subject) {
        Sentry.captureException(
            new Error(`Server-nest migration: No subject found for QotD question ${question.id}`)
        )
        return fetchParseQotDQuestion()
    }

    return mapNestQotDQuestion(question, subject)
}

const fetchCurrentQotDQuestion = async () => {
    await fetchLoadable(qotdModule.state.question, async () => {
        // Ensures we have the examMetadata and quizzes that we need
        await Promise.all([
            userModule.actions.fetchUserData(),
            questionModule.actions.fetchSerialQuestionInfoLib(),
        ])

        const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
        if (!currentExam) {
            return
        }
        const examGuid = currentExam.examGuid
        const version = currentExam.version
        const examMetadataId = currentExam.objectId

        return fetchQotDQuestion({
            examGuid,
            examMetadataId,
            version,
        })
    })
}

const fetchQotDMetric = async ({ examGuid, serial }: { examGuid: string; serial: string }) => {
    // We can't use fetchGlobalQuestionMetrics because that requires a user
    const globalMetric = await new Parse.Query<Study.Class.GlobalQuestionMetric>('GlobalQuestionMetric')
        .equalTo('examGuid', examGuid)
        .containedIn('questionSerial', [ serial ])
        .find()

    return (globalMetric?.[0]?.toJSON()) || null
}

const fetchCurrentQotDMetric = async () => {
    await fetchLoadable(qotdModule.state.globalMetric, async () => {
        // Ensures we have the examMetadata and quizzes that we need
        await Promise.all([
            userModule.actions.fetchUserData(),
            questionModule.actions.fetchSerialQuestionInfoLib(),
            fetchCurrentQotDQuestion(),
        ])
        const qotdQuestion = qotdModule.getters.getQotDQuestion()
        if (!qotdQuestion) {
            return
        }
        const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
        if (!currentExam) {
            throw new Error('No current exam found')
        }
        const examGuid = currentExam.examGuid

        return fetchQotDMetric({ examGuid, serial: qotdQuestion?.serial })
    })
}

const fetchSharedQotD = async ({ examGuid, examMetadataId, version }: {
    examGuid: string
    examMetadataId?: string
    version: string
}) => {
    const today = new Date()
    const daysSinceEpoch = Math.floor((today.getTime() / 60000 - today.getTimezoneOffset()) / 60 / 24)
    const sharedQotDInfo = await runCloudFunction<Study.Cloud.fetchSharedQotdV3>('fetchSharedQotD-v3', {
        daysSinceEpoch,
        examGuid,
        examMetadataId,
        version,
        includeMatrixQuestions: true,
        includeMPMCQuestions: true,
        includeBuildListQuestions: true,
    })
    return sharedQotDInfo
}

const recordQotD = async (answer: Study.Cloud.IQuizAnswer) => {
    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
    if (!currentExam) {
        throw new Error('No current exam found')
    }
    const examGuid = currentExam.examGuid

    const recordPayload: Study.Cloud.IRecordQuizPayload = {
        durationSeconds: 0,
        examGuid,
        mode: -1,
        platform: 'Web',
        appBundleId: 'study.pocketprep.com',
        startedAt: localISODateString(),
        answers: [ answer ],
        // Removing this because readiness-report experiment v1 is done, but may use for v2 in 2026
        // includeReadiness: true,
    }
    const recordQuizResponse = await runCloudFunction<Study.Cloud.recordQuiz>('recordQotd', recordPayload)

    quizModule.state.quizzes.push(recordQuizResponse.quiz)

    resetLoadable(qotdModule.state.globalMetric)
    await Promise.all([
        quizModule.actions.fetchAnsweredQuestions(recordQuizResponse.quiz.objectId),
        fetchCurrentQotDMetric(),
    ])

    await analyticsModule.actions.googleAnalyticsTrack(answer.isCorrect ? 'Correct' : 'Incorrect', {
        'event_category': 'QOTD',
        'event_label': currentExam.nativeAppName,
    })
}

const saveSharedQotDDraft = (sharedQotDQuizDraft: TQuizDraft) => {
    qotdModule.state.sharedQotDQuizDraft = sharedQotDQuizDraft
    localStorage.setItem('sharedQotDQuizDraft', JSON.stringify(sharedQotDQuizDraft))
}

const clearSharedQotDDraft = () => {
    qotdModule.state.sharedQotDQuizDraft = null
    localStorage.removeItem('sharedQotDQuizDraft')
}

export default {
    fetchCurrentQotDQuestion,
    fetchCurrentQotDMetric,
    fetchQotDMetric,
    fetchQotDQuestion,
    fetchSharedQotD,
    recordQotD,
    saveSharedQotDDraft,
    clearSharedQotDDraft,
}
